import React from 'react'
import { styled, keyframes, type CSS } from 'styles/stitches.config'

type LoaderProps = React.HTMLAttributes<unknown> & {
  css?: CSS
  as?: keyof JSX.IntrinsicElements
  children?: React.ReactNode
}

export const Loader = ({ children, ...props }: LoaderProps) => {
  const ariaLabel = children ? '' : 'Loader'
  return (
    <StyledLoaderContainer {...props}>
      <>
        <StyledLoader className="spinner" aria-label={ariaLabel}>
          <i className="_1" />
          <i className="_2" />
          <i className="_3" />
        </StyledLoader>
        {children && <StyledLoaderLabel className="spinner-label">{children}</StyledLoaderLabel>}
      </>
    </StyledLoaderContainer>
  )
}

const rotate = keyframes({
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
})

const StyledLoaderContainer = styled('div', {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  $$loadingColor: '$colors$white',
  $$loadingTextColor: '$colors$text',
})

const StyledLoader = styled('span', {
  'position': 'relative',
  'inset': '0px',
  'alignItems': 'center',
  'justifyContent': 'center',
  'backgroundColor': 'transparent',
  'userSelect': 'none',
  'display': 'flex',
  'borderRadius': '$rounded',
  '$$loadingSize': '$space$3',
  '$$loadingBorder': 'calc($space$1)',
  'size': '$$loadingSize',
  'i': {
    top: '0px',
    size: '100%',
    position: 'absolute',
    borderRadius: 'inherit',
  },
  '._1': {
    border: '$$loadingBorder solid $$loadingColor',
    borderTop: '$$loadingBorder solid transparent',
    borderLeft: '$$loadingBorder solid transparent',
    borderRight: '$$loadingBorder solid transparent',
    animation: `${rotate} 0.8s ease infinite`,
  },
  '._2': {
    border: '$$loadingBorder dotted $$loadingColor',
    borderTop: '$$loadingBorder solid transparent',
    borderLeft: '$$loadingBorder solid transparent',
    borderRight: '$$loadingBorder solid transparent',
    animation: `${rotate} 0.8s linear infinite`,
    opacity: 0.5,
  },
  '._3': {
    display: 'none',
  },
})

const StyledLoaderLabel = styled('label', {
  'color': '$black',
  'fontSize': '$space$6',
  '*': {
    margin: 0,
  },
})
